import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  createRouteFilter,
  deleteRouteFilter,
  getRouteFilterSources,
  listRouteFilters,
  updateRouteFilter,
} from 'api/routes/routeFiltersApi';
import { Destination } from 'components/common/ObjectTypes';
import { FlowDirectionKey } from '../flowMap/flowMapTypes';
import {
  SourceFilter,
  SourceFilterDraft,
  SourceSummary,
  sourceFilterKey,
} from '../sourceFilters/sourceFilterTypes';

// Matches the backend's default page size for this sub-resource.
const SOURCE_FILTER_PAGE_SIZE = 50;

// SOURCE FILTERS (GUNDI-5178) — the whitelist/blacklist rules on this route's arrows.
//
// One request per route returns every rule; they are keyed here by destination and
// direction, the pair a rule belongs to. Only the outgoing direction is offered for
// now, but the key keeps the shape the reverse route would need. Saves and deletes
// act on the flow the Rules panel is open on.
export const useRouteSourceFilters = ({
  getToken,
  defaultRouteId,
  rulesPanelDestination,
  rulesDirection,
  onDone,
}: {
  // The session token, or undefined once it has expired.
  getToken: () => string | undefined;
  defaultRouteId: string | undefined;
  rulesPanelDestination: Destination | null;
  rulesDirection: FlowDirectionKey;
  // Closes the Rules panel once a save or delete has landed.
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  // Load once the session is usable, not again on every token refresh.
  const tokenReady = !!getToken();
  const [sourceFilters, setSourceFilters] = useState<
    Record<string, SourceFilter>
  >({});

  useEffect(() => {
    const token = getToken();
    if (!defaultRouteId || !token) return;
    let cancelled = false;
    listRouteFilters(token, defaultRouteId)
      .then((filters) => {
        if (cancelled) return;
        const next: Record<string, SourceFilter> = {};
        filters.forEach((filter) => {
          next[sourceFilterKey(filter.destination.id, 'out')] = filter;
        });
        setSourceFilters(next);
      })
      .catch(() => {
        // Rendering "no rules" over a failed load invites recreating one into a 409.
        if (!cancelled)
          toast.error(
            `${t('Something went wrong. Please try again or contact support.')}`,
            { position: toast.POSITION.TOP_RIGHT },
          );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRouteId, tokenReady]);

  // The device list is replaced wholesale — there is no per-device endpoint — so the
  // draft carries the complete set and this sends it in one request.
  const handleSaveSourceFilter = async (draft: SourceFilterDraft) => {
    const site = rulesPanelDestination;
    // Source filters ship on the OUTGOING arrow only (frontend proposal, decision 4):
    // the reverse flow is a different Route record, and saving against defaultRouteId
    // here would filter the forward flow while claiming to filter the reverse one.
    // Resolving here would read as success to the panel (it closes the editor), so a
    // failed precondition toasts and THROWS — same reasoning as handleSaveRules.
    const token = getToken();
    if (rulesDirection !== 'out' || !site || !defaultRouteId || !token) {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source filter save preconditions not met');
    }
    const key = sourceFilterKey(site.id, rulesDirection);
    const existing = sourceFilters[key];
    const source_ids = draft.sources.map((source) => source.id);
    try {
      const saved = existing
        ? await updateRouteFilter(token, defaultRouteId, existing.id, {
            mode: draft.mode,
            source_ids,
          })
        : await createRouteFilter(token, defaultRouteId, {
            destination: site.id,
            mode: draft.mode,
            source_ids,
          });
      setSourceFilters((prev) => ({ ...prev, [key]: saved }));
      onDone();
    } catch (err: any) {
      // The backend answers 409 when the destination already carries a rule of
      // this type — which here means somebody saved one while this panel was open.
      toast.error(
        err?.response?.status === 409
          ? `${t('This destination already has a source rule. Reload the page to see it.')}`
          : `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw err;
    }
  };

  const handleDeleteSourceFilter = async () => {
    const site = rulesPanelDestination;
    const token = getToken();
    if (rulesDirection !== 'out' || !site || !defaultRouteId || !token) {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source filter delete preconditions not met');
    }
    const key = sourceFilterKey(site.id, rulesDirection);
    const existing = sourceFilters[key];
    // Already gone (removed elsewhere): nothing left to delete, so the editor
    // has nothing to show either.
    if (!existing) {
      onDone();
      return;
    }
    try {
      await deleteRouteFilter(token, defaultRouteId, existing.id);
    } catch (err) {
      // The rule still exists — confirming the delete and dropping local state
      // would make it reappear on reload as a ghost.
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw err;
    }
    setSourceFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    toast.success(`${t('Rule deleted')}`, {
      position: toast.POSITION.TOP_RIGHT,
    });
    onDone();
  };

  // Every page of the rule's devices, not just the first. Saving replaces the whole
  // list, so an editor opened on a truncated set would silently drop the rest.
  const loadSourceFilterSources = async (
    filterId: string,
  ): Promise<SourceSummary[]> => {
    const token = getToken();
    if (!defaultRouteId || !token) {
      throw new Error("No route or session to load the rule's devices with");
    }
    const all: SourceSummary[] = [];
    let offset = 0;
    for (;;) {
      const page = await getRouteFilterSources(
        token,
        defaultRouteId,
        filterId,
        {
          limit: SOURCE_FILTER_PAGE_SIZE,
          offset,
        },
      );
      all.push(...page.results);
      // Advance by what actually arrived: the server may clamp pages below the
      // requested size, and a constant stride would skip the difference.
      offset += page.results.length;
      if (all.length >= page.count) return all;
      if (page.results.length === 0) {
        // Saving would replace the rule's whole list with this partial one,
        // silently dropping every device the pages never delivered.
        throw new Error("The rule's device list came back incomplete");
      }
    }
  };

  // Destinations carrying a device-filter rule. Forward direction only — the only
  // one the editor can save a filter for — and it feeds the same arrow badge.
  const destinationsWithDeviceFilters = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    Object.entries(sourceFilters).forEach(([key, filter]) => {
      if (!filter) return;
      const [destId, direction] = key.split('|');
      if (direction === 'out') ids.add(destId);
    });
    return ids;
  }, [sourceFilters]);

  const getSourceFilter = (destId: string, direction: string) =>
    sourceFilters[sourceFilterKey(destId, direction)] ?? null;

  return {
    destinationsWithDeviceFilters,
    getSourceFilter,
    // The rule on the flow the Rules panel is open on.
    currentSourceFilter: rulesPanelDestination
      ? getSourceFilter(rulesPanelDestination.id, rulesDirection)
      : null,
    handleSaveSourceFilter,
    handleDeleteSourceFilter,
    loadSourceFilterSources,
  };
};
